import { useEffect, useRef, useState } from "react";
import { BrowserRouter } from "react-router-dom";
import { HashLink } from "react-router-hash-link";
import { Container } from "./styles";
import ScrollAnimation from "react-animate-on-scroll";
import mobizImage from "../../assets/project-mobiz.webp";
import posImage from "../../assets/project-pos.webp";
import okDeliveryPhone from "../../assets/project-ok-delivery.webp";
import okDeliveryIcon from "../../assets/ok-delivery-icon.webp";
import { useLanguage } from "../../i18n/LanguageContext";
import { renderInlineCode } from "../../i18n/renderInlineCode";
import { Dictionary } from "../../i18n/en";

// Structure only: every user-visible string lives in the i18n dictionaries, looked up by project id
type LinkKind = "googlePlay" | "appStore" | "github" | "demo" | "npm" | "website" | "caseStudy";

interface ProjectLink {
  kind: LinkKind;
  href: string;
}

type FeaturedId = keyof Dictionary["portfolio"]["featured"];
type OssId = keyof Dictionary["portfolio"]["openSource"];
type SideId = keyof Dictionary["portfolio"]["side"];

interface Project<Id extends string> {
  id: Id;
  tech: string[];
  links: ProjectLink[];
}

interface FeaturedProject extends Project<FeaturedId> {
  // Landscape screenshot shown on top of the card
  image?: string;
  // Portrait app screenshot, rendered as a phone peeking up from the bottom of the card
  phone?: string;
  // App icon shown next to the title
  icon?: string;
}

type TerminalLine =
  | { kind: "cmd"; text: string }
  | { kind: "out" | "q" | "answer" | "ok" };

interface OssProject extends Project<OssId> {
  install?: string;
  terminal: TerminalLine[];
}

// Loose views over the per-project dictionary entries (the dictionaries themselves stay strict)
interface FeaturedCopy {
  title: string;
  stat: string;
  description: string;
  mediaAlt: string;
  facts?: { value: string; label: string }[];
}

interface OssCopy {
  title: string;
  meta: string[];
  description: string;
  installNote?: string;
  terminal: Partial<Record<"out" | "q" | "answer" | "ok", string>>;
}

const CASE_STUDY_HASH = "#case-ok-delivery";
const CDD_ID: OssId = "cdd";
const CDD_FALLBACK_VERSION = "v0.9.5";
const CDD_REGISTRY_URL = "https://registry.npmjs.org/comprehension-driven/latest";
const COPIED_FEEDBACK_MS = 2000;

const featured: FeaturedProject[] = [
  {
    id: "ok-delivery",
    tech: ["React Native", "Firebase", "Redux Toolkit", "Google Maps", "Jest"],
    links: [
      {
        kind: "googlePlay",
        href: "https://play.google.com/store/apps/details?id=io.OkdeliveryApp.app",
      },
      { kind: "caseStudy", href: CASE_STUDY_HASH },
    ],
    phone: okDeliveryPhone,
    icon: okDeliveryIcon,
  },
  {
    id: "mobiz",
    tech: ["React", "TypeScript", "Supabase", "Leaflet", "Zustand", "Tailwind CSS", "PWA"],
    links: [{ kind: "website", href: "https://mobiz.zeconds.dev/" }],
    image: mobizImage,
  },
  {
    id: "zeconds-pos",
    tech: ["React", "TypeScript", "Supabase", "ZXing", "Tailwind CSS", "Vitest"],
    links: [{ kind: "website", href: "https://pos.zeconds.dev/" }],
    image: posImage,
  },
];

const openSource: OssProject[] = [
  {
    id: CDD_ID,
    install: "npx comprehension-driven init",
    tech: ["Node.js", "Git hooks", "GitHub Actions"],
    links: [
      { kind: "npm", href: "https://www.npmjs.com/package/comprehension-driven" },
      { kind: "github", href: "https://github.com/hdarioDev/comprehension-driven" },
    ],
    terminal: [
      { kind: "cmd", text: 'git commit -m "add login rate limiter"' },
      { kind: "out" },
      { kind: "q" },
      { kind: "answer" },
      { kind: "ok" },
    ],
  },
  {
    id: "voice-screen-assistant",
    tech: ["Python", "Whisper", "Vision LLMs", "Piper TTS", "Ollama"],
    // TODO(owner): add { kind: "demo", href } once the demo video is recorded
    links: [{ kind: "github", href: "https://github.com/hdarioDev/voice-screen-assistant" }],
    terminal: [{ kind: "cmd", text: "bash run.sh" }, { kind: "out" }, { kind: "ok" }],
  },
];

const sideProjects: Project<SideId>[] = [
  {
    id: "my-shop-online",
    tech: ["Next.js", "React", "Tailwind CSS", "Supabase"],
    links: [{ kind: "website", href: "https://shop.zeconds.dev/" }],
  },
  {
    id: "trading-signals",
    tech: ["Python", "FastAPI", "WebSocket", "Uvicorn", "NumPy"],
    links: [{ kind: "github", href: "https://github.com/hdarioDev/trading-signals-ema-api" }],
  },
  {
    id: "netflix-clone",
    tech: ["React", "TypeScript", "styled-components", "Webpack"],
    links: [
      { kind: "demo", href: "https://fantastic-frangipane-04752c.netlify.app/" },
      { kind: "github", href: "https://github.com/hdarioDev/my-clon-netflix" },
    ],
  },
];

function ProjectLinkAnchor({ title, link }: { title: string; link: ProjectLink }) {
  const { t } = useLanguage();
  const label = t.portfolio.linkLabels[link.kind];

  // In-page link: same pill, no new tab
  if (link.kind === "caseStudy") {
    return (
      <BrowserRouter>
        <HashLink smooth to={link.href}>
          {label} <span aria-hidden="true">→</span>
        </HashLink>
      </BrowserRouter>
    );
  }

  let ariaLabel = t.portfolio.linkAria.onPlatform(title, label);
  if (link.kind === "demo") ariaLabel = t.portfolio.linkAria.demo(title);
  if (link.kind === "website") ariaLabel = t.portfolio.linkAria.website(title);

  return (
    <a href={link.href} target="_blank" rel="noreferrer" aria-label={ariaLabel}>
      {label} <span aria-hidden="true">↗</span>
    </a>
  );
}

function TechAndLinks({ title, project }: { title: string; project: Project<string> }) {
  return (
    <>
      <ul className="tech-list">
        {project.tech.map((tech) => (
          <li key={tech}>{tech}</li>
        ))}
      </ul>
      {project.links.length > 0 && (
        <ul className="project-links">
          {project.links.map((link) => (
            <li key={link.kind}>
              <ProjectLinkAnchor title={title} link={link} />
            </li>
          ))}
        </ul>
      )}
    </>
  );
}

function InstallCommand({ command }: { command: string }) {
  const { t } = useLanguage();
  const [copied, setCopied] = useState(false);
  const mounted = useRef(true);

  useEffect(() => {
    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (!copied) return undefined;
    const timer = window.setTimeout(() => setCopied(false), COPIED_FEEDBACK_MS);
    return () => window.clearTimeout(timer);
  }, [copied]);

  function handleCopy() {
    // Clipboard API is missing on insecure origins and old browsers: keep the default label
    if (!navigator.clipboard || !navigator.clipboard.writeText) return;
    navigator.clipboard
      .writeText(command)
      .then(() => {
        if (mounted.current) setCopied(true);
      })
      .catch(() => undefined);
  }

  return (
    <div className="install">
      <code>{command}</code>
      <button type="button" className="copy-button" onClick={handleCopy}>
        <span aria-live="polite">{copied ? t.portfolio.copied : t.portfolio.copy}</span>
      </button>
    </div>
  );
}

function OssCard({ project, version }: { project: OssProject; version?: string }) {
  const { t } = useLanguage();
  const copy: OssCopy = t.portfolio.openSource[project.id];
  const meta = version ? [version, ...copy.meta] : copy.meta;

  return (
    <article id={project.id} className="project oss-card">
      <div className="terminal" role="img" aria-label={t.portfolio.terminalLabel(copy.title)}>
        <div className="terminal-bar">
          <span />
          <span />
          <span />
        </div>
        <div className="terminal-body">
          {project.terminal.map((line, index) => (
            <div key={index} className={`terminal-line terminal-${line.kind}`}>
              {line.kind === "cmd" && <span className="terminal-prompt">$ </span>}
              {line.kind === "cmd" ? line.text : copy.terminal[line.kind]}
            </div>
          ))}
        </div>
      </div>
      <h3>{copy.title}</h3>
      <p className="oss-meta">{meta.join(" · ")}</p>
      <div className="description">
        {copy.description.split("\n\n").map((paragraph) => (
          <p key={paragraph}>{paragraph}</p>
        ))}
      </div>
      {project.install && <InstallCommand command={project.install} />}
      {copy.installNote && <p className="install-note">{renderInlineCode(copy.installNote)}</p>}
      <TechAndLinks title={copy.title} project={project} />
    </article>
  );
}

function FeaturedCard({ project, lead }: { project: FeaturedProject; lead: boolean }) {
  const { t } = useLanguage();
  const copy: FeaturedCopy = t.portfolio.featured[project.id];

  const classNames = ["project"];
  if (lead) classNames.push("project-lead");
  if (project.phone) classNames.push("has-phone");

  const heading = (
    <>
      <h3>{copy.title}</h3>
      <p className="stat">{copy.stat}</p>
    </>
  );

  const content = (
    <>
      {project.image && (
        <div className="project-media">
          <img src={project.image} alt={copy.mediaAlt} loading="lazy" width={960} height={500} />
        </div>
      )}
      {project.icon ? (
        <div className="app-title">
          {/* Decorative: the title follows */}
          <img src={project.icon} alt="" width={56} height={56} />
          <div>{heading}</div>
        </div>
      ) : (
        heading
      )}
      <p className="description">{copy.description}</p>
      {copy.facts && (
        <dl className="facts">
          {copy.facts.map((fact) => (
            <div className="fact" key={fact.label}>
              <dt>{fact.label}</dt>
              <dd>{fact.value}</dd>
            </div>
          ))}
        </dl>
      )}
      <TechAndLinks title={copy.title} project={project} />
    </>
  );

  return (
    <article id={project.id} className={classNames.join(" ")}>
      {project.phone ? (
        <>
          <div className="project-content">{content}</div>
          <div className="project-phone">
            <img src={project.phone} alt={copy.mediaAlt} loading="lazy" width={560} height={1179} />
          </div>
        </>
      ) : (
        content
      )}
    </article>
  );
}

export function Portfolio() {
  const { t } = useLanguage();
  const [cddVersion, setCddVersion] = useState(CDD_FALLBACK_VERSION);

  useEffect(() => {
    let cancelled = false;
    fetch(CDD_REGISTRY_URL)
      .then((response) => (response.ok ? response.json() : null))
      .then((data) => {
        if (!cancelled && data && typeof data.version === "string") {
          setCddVersion(`v${data.version}`);
        }
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <Container id="portfolio">
      <h2>{t.portfolio.heading}</h2>

      <div className="featured">
        {featured.map((project, index) => (
          <ScrollAnimation animateIn="fadeInUp" key={project.id}>
            <FeaturedCard project={project} lead={index === 0} />
          </ScrollAnimation>
        ))}
      </div>

      <div className="oss">
        <h2 className="oss-heading">{t.portfolio.ossHeading}</h2>
        <p className="oss-intro">{t.portfolio.ossIntro}</p>
        <div className="oss-grid">
          {openSource.map((project) => (
            <ScrollAnimation animateIn="fadeInUp" key={project.id}>
              <OssCard project={project} version={project.id === CDD_ID ? cddVersion : undefined} />
            </ScrollAnimation>
          ))}
        </div>
      </div>

      <h3 className="side-title">{t.portfolio.sideHeading}</h3>
      <ul className="side-list">
        {sideProjects.map((project) => {
          const copy = t.portfolio.side[project.id];
          return (
            <li key={project.id} id={project.id}>
              <span className="side-name">{copy.title}</span>
              <div className="side-body">
                <p>{copy.description}</p>
                <p className="side-tech">{project.tech.join(" · ")}</p>
              </div>
              <div className="side-links">
                {project.links.map((link) => (
                  <ProjectLinkAnchor key={link.kind} title={copy.title} link={link} />
                ))}
              </div>
            </li>
          );
        })}
      </ul>
    </Container>
  );
}
